import { useEffect, useState } from 'react';
import axios from '../../api/axios';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentLayout from '../ContentLayout/ContentLayout';
import { useAuth } from '../../context/AuthContext';
import { AddTeamUser } from './AddTeamUser';
import { EditTeamUser } from './EditTeamUser';

const GET_USERS_BY_ORG = '/createuser';

export const Team = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editMember, setEditMember] = useState(null);
  const [menuType, setMenuType] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false)

  const allowedRoles = ['superadmin', 'admin'];
  const auth = useAuth();
  const canUseMenu = allowedRoles.includes(auth.userRole.toLowerCase());

//   <AddTeamUser 
//   drawerOpen={drawerOpen} 
//   setDrawerOpen={setDrawerOpen} 
//   addUserToState={(newUser) => setUsers(prev => [...prev, newUser])} 
// />

  useEffect(() => {
    axios.post(GET_USERS_BY_ORG, {
      getUsersByOrg: true,
      orgId: localStorage.getItem('orgId')
    })
      .then((res) => {
        const sortedUsers = (res.data?.users || []).sort((a, b) => {
        const lastCompare = a.last.localeCompare(b.last, undefined, { sensitivity: 'base' });
        if (lastCompare !== 0) return lastCompare;
        return a.first.localeCompare(b.first, undefined, { sensitivity: 'base' });
      });
        setUsers(sortedUsers);
      })
      .catch((err) => {
        console.error("Error fetching users:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleUserAdded = (newUser) => {
    setUsers((prev) => [...prev, newUser]); // instantly adds new user to UI
  };

  const handleMenuOpen = (event, member, type) => {
    setAnchorEl(event.currentTarget);
    setEditMember(member);
    setMenuType(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuType(null);
  };

  // Callback function to handle user updates from EditTeamUser
  const handleUserUpdated = (updatedUser, id) => {
      setUsers(prevUsers => 
      prevUsers.map(user => 
          user.id === id ? updatedUser : user
      )
      );
  };

  const handleDeactivateProfile = async () => {
    if (!editMember) return;

    console.log("editMember ", editMember.id)
    console.log("localStorage ", localStorage.getItem('orgId'))

    try {
      // Call your API to update user status
      const response = await axios.post(GET_USERS_BY_ORG, {
        updateUserStatus: true,
        userId: editMember.id,  // or selectedMember.userId depending on your data shape
        status: "inactive",         // or "deactivated" if that is your status value
        orgId: localStorage.getItem('orgId')
      });

      if (response.data.code === 0) {
        // Update local users state by mapping through users and updating the status of selected user
        setUsers((prevUsers) => {
        const updatedUsers = prevUsers.map((user) =>
          user.id === editMember.id
            ? { ...user, status: "inactive", isArchived: true }
            : user
        );

        return updatedUsers.sort((a, b) => {
          const lastCompare = a.last.localeCompare(b.last, undefined, { sensitivity: 'base' });
          if (lastCompare !== 0) return lastCompare;
          return a.first.localeCompare(b.first, undefined, { sensitivity: 'base' });
        });
      });
        // Close the menu
        handleMenuClose();
      } else {
        console.error("Failed to update user status:", response.data.message);
      }
    } catch (error) {
      console.error("Error updating user status:", error);
    }
  };

  const handleReactivateMember = async () => {
    if (!editMember) return;

    try {
      const response = await axios.post(GET_USERS_BY_ORG, {
        updateUserStatus: true,
        userId: editMember.id,
        status: "active",
        orgId: localStorage.getItem("orgId")
      });

      if (response.data.code === 0) {
        setUsers((prevUsers) => {
        const updatedUsers = prevUsers.map((user) =>
          user.id === editMember.id ? { ...user, status: "active" } : user
        );

        return updatedUsers.sort((a, b) => {
          const lastCompare = a.last.localeCompare(b.last, undefined, { sensitivity: "base" });
          return lastCompare !== 0
            ? lastCompare
            : a.first.localeCompare(b.first, undefined, { sensitivity: "base" });
        });
      });
      } else {
        console.error("Failed to update status:", response.data.message);
      }
    } catch (err) {
      console.error("Error updating status:", err);
    } finally {
      handleMenuClose();
    }
  };

  const handleEditProfile = () => {
    setEditDrawerOpen(true)
    handleMenuClose();
  };

  const handleAddMember = () => {
    setDrawerOpen(true);
  };

  const activeMembers = users.filter((u) => u.status === "active");
  const deactivatedMembers = users.filter((u) => u.status === "inactive");

  const getRoleDisplayName = (role) => {
    const roleMap = {
      superadmin: "Super Admin",
      admin: "Admin",
      useredit: "Editor",
      user: "Viewer"
    };
    
    return roleMap[role] || role; // fallback to original value if not found
  };

  return (
    <ContentLayout title="Team" url="/plans">
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 3 }}>
        {canUseMenu && (
          <Button variant="text" startIcon={<AddIcon />} onClick={handleAddMember}>
            ADD MEMBER
          </Button>
        )}
      </Box>

      {/* Table */}
      <Table>
        <TableHead>
          {activeMembers.length > 0 && (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Active
                </Typography>
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {activeMembers.map((member, index) => (
            <TableRow key={`active-${index}`} hover>
              <TableCell>{`${member.first} ${member.last}`}</TableCell>
              <TableCell>{member.email}</TableCell>
              <TableCell>{getRoleDisplayName(member.role)}</TableCell>
              <TableCell align="right">
                {canUseMenu && !(auth.userRole.toLowerCase() === 'admin' && member.role.toLowerCase() === 'superadmin') && (
                    <IconButton onClick={(e) => handleMenuOpen(e, member, 'active')}>
                    <MoreVertIcon />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>

        <TableHead>
          {canUseMenu && deactivatedMembers.length > 0 && (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Deactivated
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {deactivatedMembers.map((member, index) => (
            <TableRow key={`active-${index}`} hover>
              <TableCell>{`${member.first} ${member.last}`}</TableCell>
              <TableCell>{member.email}</TableCell>
              <TableCell>{getRoleDisplayName(member.role)}</TableCell>
              <TableCell align="right">
                {canUseMenu && !(auth.userRole.toLowerCase() === 'admin' && member.role.toLowerCase() === 'superadmin') && (
                    <IconButton onClick={(e) => handleMenuOpen(e, member, 'inactive')}>
                    <MoreVertIcon />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {menuType === 'active' && [
            <MenuItem key="edit" onClick={handleEditProfile}>
                Edit Profile
              </MenuItem>,
              editMember.id !== localStorage.getItem("id") && (
                <MenuItem
                  key="deactivate"
                  onClick={handleDeactivateProfile}
                  sx={{ color: '#D32F2F' }}
                >
                  Deactivate Member
                </MenuItem>
              ),
          ]}
          {menuType === 'inactive' && (
            <MenuItem onClick={handleReactivateMember}>Reactivate Member</MenuItem>
          )}
        </Menu>

      </Table>

      {/* Drawer for Add Member */}
      <AddTeamUser drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} onUserAdded={handleUserAdded} />
      <EditTeamUser drawerOpen={editDrawerOpen} setDrawerOpen={setEditDrawerOpen} editMember={editMember} onUserUpdated={handleUserUpdated} handleDeactivateProfile={handleDeactivateProfile}/>
      
    </ContentLayout>
  );
};
